import React, { useEffect, useRef, useState } from 'react';
import '../styles/CursorGlow.css';

const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';
const NODE_SPACING = 128;
const LINK_DISTANCE = 186;
const SCROLL_PARALLAX = 0.055;

function buildArchitecture(width, height) {
    const nodes = [];
    const cols = Math.ceil(width / NODE_SPACING) + 2;
    const rows = Math.ceil(height / NODE_SPACING) + 2;

    for (let row = -1; row < rows; row += 1) {
        for (let col = -1; col < cols; col += 1) {
            const seed = (col * 92821 + row * 68917) % 9973;
            const jitterX = Math.sin(seed) * 28;
            const jitterY = Math.cos(seed * 1.7) * 24;
            const baseX = col * NODE_SPACING + jitterX;
            const baseY = row * NODE_SPACING + jitterY;

            nodes.push({
                baseX,
                baseY,
                x: baseX,
                y: baseY,
                phase: seed / 9973,
            });
        }
    }

    const links = [];
    for (let i = 0; i < nodes.length; i += 1) {
        for (let j = i + 1; j < nodes.length; j += 1) {
            const dx = nodes[i].baseX - nodes[j].baseX;
            const dy = nodes[i].baseY - nodes[j].baseY;
            const distance = Math.hypot(dx, dy);

            if (distance < LINK_DISTANCE) {
                links.push([i, j, distance]);
            }
        }
    }

    return { nodes, links };
}

export default function CursorGlow() {
    const canvasRef = useRef(null);
    const rafRef = useRef(null);
    const architecture = useRef({ nodes: [], links: [] });
    const scroll = useRef({ current: 0, target: 0 });
    const [graphEnabled, setGraphEnabled] = useState(true);

    useEffect(() => {
        if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;

        const reducedMotion = window.matchMedia(REDUCED_MOTION_QUERY);

        const syncEnabled = () => {
            setGraphEnabled(!reducedMotion.matches);
        };

        syncEnabled();
        reducedMotion.addEventListener('change', syncEnabled);

        return () => {
            reducedMotion.removeEventListener('change', syncEnabled);
        };
    }, []);

    useEffect(() => {
        if (!graphEnabled) return undefined;

        const resizeCanvas = () => {
            const canvas = canvasRef.current;
            if (!canvas) return;

            const width = window.innerWidth;
            const height = window.innerHeight;
            const dpr = Math.min(window.devicePixelRatio || 1, 1.75);

            canvas.width = Math.round(width * dpr);
            canvas.height = Math.round(height * dpr);
            canvas.style.width = `${width}px`;
            canvas.style.height = `${height}px`;

            const context = canvas.getContext('2d');
            context.setTransform(dpr, 0, 0, dpr, 0, 0);
            architecture.current = buildArchitecture(width, height);
        };

        const drawArchitecture = (time) => {
            const canvas = canvasRef.current;
            const context = canvas?.getContext('2d');
            if (!canvas || !context) return;

            const width = window.innerWidth;
            const height = window.innerHeight;
            const { nodes, links } = architecture.current;

            context.clearRect(0, 0, width, height);
            scroll.current += (scroll.target - scroll.current) * 0.07;
            const scrollOffset = (scroll.current * SCROLL_PARALLAX) % NODE_SPACING;

            nodes.forEach((node) => {
                const driftX = Math.sin(time * 0.00018 + node.phase * 12) * 1.8;
                const driftY = Math.cos(time * 0.00016 + node.phase * 10) * 1.4;
                const targetX = node.baseX + driftX;
                const targetY = node.baseY + driftY - scrollOffset;

                node.x += (targetX - node.x) * 0.035;
                node.y += (targetY - node.y) * 0.035;
            });

            links.forEach(([startIndex, endIndex, distance]) => {
                const start = nodes[startIndex];
                const end = nodes[endIndex];
                const middleX = (start.x + end.x) * 0.5;
                const middleY = (start.y + end.y) * 0.5;
                const shimmer = (Math.sin(time * 0.00025 + middleX * 0.018 + middleY * 0.014) + 1) * 0.5;

                context.beginPath();
                context.moveTo(start.x, start.y);
                context.lineTo(end.x, end.y);
                context.lineWidth = 0.5;
                context.strokeStyle = `rgba(148, 163, 200, ${0.07 + (1 - distance / LINK_DISTANCE) * 0.05 + shimmer * 0.025})`;
                context.stroke();
            });

            nodes.forEach((node) => {
                const shimmer = (Math.sin(time * 0.00032 + node.phase * 18) + 1) * 0.5;

                context.beginPath();
                context.arc(node.x, node.y, 1.15 + shimmer * 0.35, 0, Math.PI * 2);
                context.fillStyle = `rgba(148, 163, 200, ${0.16 + shimmer * 0.08})`;
                context.fill();
            });
        };

        const animate = (time) => {
            drawArchitecture(time);
            rafRef.current = window.requestAnimationFrame(animate);
        };

        const handleScroll = () => {
            scroll.target = window.scrollY || 0;
        };

        resizeCanvas();
        handleScroll();
        rafRef.current = window.requestAnimationFrame(animate);
        window.addEventListener('resize', resizeCanvas);
        window.addEventListener('scroll', handleScroll, { passive: true });

        return () => {
            window.removeEventListener('resize', resizeCanvas);
            window.removeEventListener('scroll', handleScroll);
            window.cancelAnimationFrame(rafRef.current);
        };
    }, [graphEnabled]);

    if (!graphEnabled) return null;

    return (
        <canvas ref={canvasRef} className="antigravity-architecture" aria-hidden="true" />
    );
}
